import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import LanguageToggle from './LanguageToggle';
import { normalizeLanguage } from '../../utils/languages';

/**
 * Shows a workshop what its customers will receive: the email, the SMS, the PDF and the public
 * page of one feature (feedback request, service reminder, ...) rendered by the backend for
 * sample data and the settings currently on screen.
 *
 * Backend contract (see verstaani/utils/message_preview.py): `loadPreview(language)` resolves to
 *   { language, variants: [{ key, label,
 *       email: {subject, html, text} | null, sms: {text, length} | null,
 *       page: {url, data} | null, pdf: {filename, content_base64} | null }] }
 * A feature that sends several different messages (booking: confirmation, cancellation, ...)
 * returns several variants and the dialog shows a picker; one variant shows no picker. Only
 * the channels the backend returns get a tab. The page tab is rendered by the feature itself
 * through `renderPage(page, { language })`, typically its public page component in preview
 * mode, so what the dialog shows is the real page and not a mock of it.
 *
 * Props:
 *   open, onClose
 *   title            dialog title
 *   loadPreview      (language) => Promise<preview>
 *   renderPage       (page, { language }) => node   (optional; without it there is no page tab)
 *   defaultLanguage  language the first render uses (defaults to the UI language)
 *   showLanguage     false hides the language toggle (messages that exist in one language only)
 *   intro            optional sentence above the tabs
 *   pageLabel        label of the page tab (default "Page"); e.g. "Feedback page"
 */
export default function MessagePreviewDialog({
  open,
  onClose,
  title,
  loadPreview,
  renderPage,
  defaultLanguage,
  showLanguage = true,
  intro,
  pageLabel,
}) {
  const { t, i18n } = useTranslation();
  const muiTheme = useTheme();
  const fullScreen = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const [language, setLanguage] = useState(normalizeLanguage(defaultLanguage || i18n.language));
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [variantKey, setVariantKey] = useState(null);
  const [tab, setTab] = useState(null);

  useEffect(() => {
    if (open) {
      setLanguage(normalizeLanguage(defaultLanguage || i18n.language));
      setPreview(null);
      setVariantKey(null);
      setTab(null);
      setError(false);
    }
  }, [open, defaultLanguage, i18n.language]);

  const load = useCallback(async (lang) => {
    setLoading(true);
    setError(false);
    try {
      const data = await loadPreview(lang);
      const variants = data?.variants || [];
      setPreview(data);
      // Keep the chosen variant and channel across a reload (language switch) when they still exist.
      let chosenVariant = null;
      setVariantKey((current) => {
        chosenVariant = variants.find((v) => v.key === current) || variants[0] || null;
        return chosenVariant ? chosenVariant.key : null;
      });
      setTab((current) => {
        const available = channelsOf(chosenVariant, renderPage);
        return current && available.includes(current) ? current : available[0] || null;
      });
    } catch (err) {
      console.error('Failed to load message preview:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [loadPreview, renderPage]);

  useEffect(() => {
    if (open) {
      load(language);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, language]);

  const variants = preview?.variants || [];
  const variant = variants.find((v) => v.key === variantKey) || variants[0] || null;
  const channels = channelsOf(variant, renderPage);
  const activeTab = tab && channels.includes(tab) ? tab : channels[0] || null;

  const selectVariant = (key) => {
    setVariantKey(key);
    const next = variants.find((v) => v.key === key);
    const available = channelsOf(next, renderPage);
    setTab((current) => (current && available.includes(current) ? current : available[0] || null));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      data-testid="message-preview-dialog"
    >
      <DialogTitle sx={{ pb: 1 }}>{title || t('message_preview.title')}</DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'grey.50' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            mb: 1.5,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
            {intro || t('message_preview.intro')}
          </Typography>
          {showLanguage && (
            <LanguageToggle
              size="small"
              value={language}
              onChange={(_, value) => { if (value) setLanguage(value); }}
            />
          )}
        </Box>

        {variants.length > 1 && (
          <FormControl size="small" fullWidth sx={{ mb: 1.5, bgcolor: 'background.paper' }}>
            <InputLabel>{t('message_preview.variant')}</InputLabel>
            <Select
              label={t('message_preview.variant')}
              value={variant?.key || ''}
              onChange={(e) => selectVariant(e.target.value)}
              inputProps={{ 'data-testid': 'message-preview-variant' }}
            >
              {variants.map((v) => (
                <MenuItem key={v.key} value={v.key} data-testid={`message-preview-variant-${v.key}`}>{v.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {channels.length > 1 && (
          <Tabs
            value={activeTab}
            onChange={(_, value) => setTab(value)}
            variant="fullWidth"
            sx={{ mb: 1.5, bgcolor: 'background.paper', borderRadius: 1 }}
          >
            {channels.map((channel) => (
              <Tab
                key={channel}
                value={channel}
                label={channel === 'page' && pageLabel ? pageLabel : t(`message_preview.tab_${channel}`)}
                data-testid={`message-preview-tab-${channel}`}
              />
            ))}
          </Tabs>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>{t('message_preview.load_error')}</Alert>
        )}

        {loading && !preview ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : variant && (
          <Box sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity 150ms' }}>
            {activeTab === 'email' && variant.email && <EmailPane email={variant.email} />}
            {activeTab === 'sms' && variant.sms && <SmsPane sms={variant.sms} />}
            {activeTab === 'pdf' && variant.pdf && <PdfPane pdf={variant.pdf} />}
            {activeTab === 'page' && variant.page && (
              <PagePane page={variant.page} language={language} renderPage={renderPage} />
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="message-preview-close">{t('Close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

function channelsOf(variant, renderPage) {
  if (!variant) return [];
  const channels = [];
  if (variant.email) channels.push('email');
  if (variant.sms) channels.push('sms');
  if (variant.pdf) channels.push('pdf');
  if (variant.page && renderPage) channels.push('page');
  return channels;
}

// Links in a previewed email must not go anywhere: the stars in a feedback request are links
// to a survey that does not exist for sample data. Scripts never run either.
function inertEmailHtml(html) {
  const style = '<style>a { pointer-events: none !important; cursor: default !important; }</style>';
  return html.includes('</head>') ? html.replace('</head>', `${style}</head>`) : style + html;
}

function EmailPane({ email }) {
  const { t } = useTranslation();
  return (
    <Box
      data-testid="message-preview-email"
      sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}
    >
      <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">{t('message_preview.subject')}:</Typography>
        <Typography variant="body2" fontWeight={600} data-testid="message-preview-subject">{email.subject}</Typography>
      </Box>
      <Box
        component="iframe"
        title={t('message_preview.tab_email')}
        srcDoc={inertEmailHtml(email.html)}
        sandbox=""
        sx={{ display: 'block', width: '100%', height: { xs: '60vh', sm: 560 }, border: 0, bgcolor: '#f1f5f9' }}
      />
    </Box>
  );
}

function SmsPane({ sms }) {
  const { t } = useTranslation();
  return (
    <Box data-testid="message-preview-sms" sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
      <Box sx={{ width: '100%', maxWidth: 360 }}>
        <Box
          sx={{
            bgcolor: '#e5e7eb',
            color: 'text.primary',
            borderRadius: '18px',
            borderBottomLeftRadius: '4px',
            px: 2,
            py: 1.25,
            fontSize: 15,
            lineHeight: 1.45,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
          data-testid="message-preview-sms-text"
        >
          {sms.text}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, ml: 1 }}>
          {t('message_preview.sms_length', { count: sms.length })}
        </Typography>
      </Box>
    </Box>
  );
}

function PdfPane({ pdf }) {
  return (
    <Box
      data-testid="message-preview-pdf"
      sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}
    >
      <Box
        component="iframe"
        title={pdf.filename}
        src={`data:application/pdf;base64,${pdf.content_base64}`}
        sx={{ display: 'block', width: '100%', height: { xs: '65vh', sm: 640 }, border: 0 }}
      />
    </Box>
  );
}

function PagePane({ page, language, renderPage }) {
  const { t } = useTranslation();
  return (
    <Box data-testid="message-preview-page">
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, textAlign: 'center', wordBreak: 'break-all' }}>
        {page.url}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, textAlign: 'center' }}>
        {t('message_preview.page_hint')}
      </Typography>
      {/* Customers open these pages on a phone: show it at phone width. */}
      <Box
        sx={{
          mx: 'auto',
          width: '100%',
          maxWidth: 420,
          height: { xs: '65vh', sm: 640 },
          overflow: 'auto',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          boxShadow: 2,
        }}
      >
        {renderPage(page, { language })}
      </Box>
    </Box>
  );
}
